import React, { useEffect, useState } from "react";
import { DatePicker, DatePickerInput } from "@carbon/react";
import { gql, useLazyQuery, useQuery } from "@apollo/client";

const GET_TODAY_QUERY = gql`
  query GetToday {
    today {
      date
      weekNumber
      dayOfWeek
      holiday {
        name
      }
    }
  }
`;

const GET_DAY_QUERY = gql`
  query GetDay($date: String!) {
    day(date: $date) {
      date
      weekNumber
      dayOfWeek
      holiday {
        name
      }
    }
  }
`;

const GET_SCHOOL_YEAR_QUERY = gql`
  query GetSchoolYearOverview {
    schoolYearOverview {
      startDate
      endDate
      weeks {
        weekNumber
        startDate
        endDate
      }
      holidays {
        name
        startDate
        endDate
      }
      normalStudentUnits
    }
  }
`;

export default function DateSelector({ onDateChange }) {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);
    
    const { data: schoolYearData, loading: schoolYearLoading } = useQuery(GET_SCHOOL_YEAR_QUERY);
    const [fetchToday, { data: todayData }] = useLazyQuery(GET_TODAY_QUERY);
    const [fetchDay, { data: fetchedDayData, loading: dayLoading, error }] = useLazyQuery(GET_DAY_QUERY);

    // Fetch school year data and today's date on initial load
    useEffect(() => {
        fetchToday();
    }, [fetchToday]);
    
    // Set min and max dates when school year data is received
    useEffect(() => {
        if (schoolYearData && schoolYearData.schoolYearOverview) {
            const { startDate, endDate } = schoolYearData.schoolYearOverview;
            setMinDate(new Date(startDate));
            setMaxDate(new Date(endDate));
        }
    }, [schoolYearData]);

    // When today's data is received, set the date
    useEffect(() => {
        if (todayData && todayData.today) {
            const serverDate = new Date(todayData.today.date);
            setSelectedDate(serverDate);
            
            // Format the date to UTC before sending it to the server
            const utcDate = new Date(Date.UTC(
                serverDate.getFullYear(), 
                serverDate.getMonth(), 
                serverDate.getDate()
            ));
            
            fetchDay({ variables: { date: utcDate.toISOString() } });
        }
    }, [todayData, fetchDay]);

    // When day data is received, call the callback
    useEffect(() => {
        if (fetchedDayData && fetchedDayData.day) {
            onDateChange && onDateChange(selectedDate, fetchedDayData.day);
        }
    }, [fetchedDayData, selectedDate, onDateChange]);

    // Handle date change from the date picker
    const handleDateChange = (selectedDates) => {
        if (selectedDates && selectedDates[0]) {
            const newDate = new Date(selectedDates[0]);
            setSelectedDate(newDate);
            
            // Format the date to UTC before sending it to the server
            const utcDate = new Date(Date.UTC(
                newDate.getFullYear(), 
                newDate.getMonth(), 
                newDate.getDate()
            ));
            
            fetchDay({ variables: { date: utcDate.toISOString() } });
        }
    };

    const loading = dayLoading || schoolYearLoading;
    
    if (loading && !selectedDate) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    return (
        <div className="date-selector-wrapper">
            <DatePicker
                datePickerType="single"
                onChange={handleDateChange}
                value={selectedDate}
                dateFormat="d.m.Y"
                minDate={minDate}
                maxDate={maxDate}
            >
                <DatePickerInput
                    id="date-picker-single"
                    labelText="Heute"
                    placeholder="dd.mm.yyyy"
                    dateFormat="d.m.Y"
                />
            </DatePicker>
        </div>
    );
}
